import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { safeArray } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import ThreadReportPreviewModal from '../../components/ThreadReportPreviewModal';
import { correspondenceMaxSizeLabel } from '../../config/attachmentLimits';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab, dataUrlToBlobUrl, partitionFilesByMaxSize, correspondenceOversizeMessage } from '../../utils/attachmentStorage';
import { downloadAttachmentsZip } from '../../utils/attachmentsZip';
import {
  correspondenceAPI,
  raBillsAPI,
  bankGuaranteesAPI,
  milestonesAPI,
  deviationsAPI,
  qaqcAPI,
  clearancesAPI,
  submittalsAPI,
  adminAPI,
} from '../../services/authService';
import { confirmDialog } from '../../utils/confirmDialog';
import { useFormConfiguration } from '../../hooks/useFormConfiguration';
import { ConfiguredField } from '../../components/ConfiguredField';
import { DynamicFieldsSection } from '../../components/DynamicFieldRenderer';
import { formatDateIN } from '../../utils/dateFormat';
import { notifySuccess, notifyError } from '../../utils/notify';
import StatusBadge from '../../components/StatusBadge';
import SubmittalStatusBadge, { formatSubmittalStatus } from '../submittals/SubmittalStatusBadge';
import ProjectNameText from '../../components/ProjectNameText';
import { DEC_INFRA } from '../../config/brandingConfig';
import { correspondenceCategories } from '../../data/mockData';
import {
  displayCorrespondenceCategory,
  getCategoryFieldConfig,
  isBankGuaranteeCategory,
  isDrawingDesignCorrespondenceCategory,
  matchesRaBillCategory,
  matchesClearanceCategory,
  matchesMilestoneEotCategory,
  matchesDeviationsExtraItemsCategory,
  matchesScopeAgreementCategory,
  matchesQualityCategory,
  normalizeCategoryForForm,
} from '../../data/categoryFieldConfig';

const formatCurrency = (val) => (
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val || 0)}`
);

const ThreadDetail = ({
  chainId,
  onBack,
  isAdmin,
  focusEntryId,
  onFocusEntryConsumed,
  onOpenDrawingsDesign,
  onViewSubmittal,
}) => {
  const normalizeMode = (value) => {
    if (value === 'Email') return 'Email';
    return 'Letter';
  };
  const isEmailCorrespondenceMode = (mode) => normalizeMode(mode) === 'Email';
  const normalizePriority = (value, fallback = 'Routine') => {
    if (['Immediate', 'Urgent', 'Routine', 'Critical'].includes(value)) return value;
    if (value === 'High') return 'Urgent';
    if (value === 'Medium' || value === 'Low') return 'Routine';
    return fallback;
  };
  const [showAppendModal, setShowAppendModal] = useState(false);
  const [showEditEntryModal, setShowEditEntryModal] = useState(false);
  const [editEntryTarget, setEditEntryTarget] = useState(null);
  const [editEntryForm, setEditEntryForm] = useState({
    ref_no: '', direction: 'Outward', from: '', to: '', letter_date: '', entry_date: '', mode: 'Letter',
    summary: '', attachments: [],
  });
  const [editEntryError, setEditEntryError] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState('timeline');
  const [entryError, setEntryError] = useState('');
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [showThreadReportPreview, setShowThreadReportPreview] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [downloadingAttachments, setDownloadingAttachments] = useState(false);
  const [apiChain, setApiChain] = useState(null);
  const [apiEntries, setApiEntries] = useState([]);
  const [useApiData, setUseApiData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const { projects: projectOptionsRaw } = useProjects();
  const projectOptions = Array.isArray(projectOptionsRaw) ? projectOptionsRaw : [];
  const [newEntry, setNewEntry] = useState({
    direction: 'Outward', ref_no: '', from: DEC_INFRA, to: '', summary: '',
    sla_days: 7, response_expected: true, mode: 'Letter', priority: 'Routine', letter_date: '', attachments: []
  });
  const [editForm, setEditForm] = useState({
    subject: '', category: 'General', severity: 'Routine', targeted_reply_days: 7,
    status: 'Open', project_id: '', last_entry_date: '', category_data: {},
  });
  const [editFormLoadingLinked, setEditFormLoadingLinked] = useState(false);
  const [linkedSubmittal, setLinkedSubmittal] = useState(null);

  const editSubjectFormConfig = useFormConfiguration({ pageKey: 'thread_detail', formKey: 'edit_subject', mode: 'edit', enabled: showEditModal });
  const appendEntryFormConfig = useFormConfiguration({ pageKey: 'thread_detail', formKey: 'append_entry', mode: 'create', enabled: showAppendModal });
  const editEntryFormConfig = useFormConfiguration({ pageKey: 'thread_detail', formKey: 'edit_entry', mode: 'edit', enabled: showEditEntryModal });
  const [subjectCustomValues, setSubjectCustomValues] = useState({});
  const [entryCustomValues, setEntryCustomValues] = useState({});
  const [editEntryCustomValues, setEditEntryCustomValues] = useState({});

  const defaultsFromCustomFields = (customFields) => {
    const out = {};
    for (const f of customFields || []) {
      if (f.defaultValue != null && f.defaultValue !== '') out[f.fieldKey] = f.defaultValue;
      else if (f.fieldType === 'boolean') out[f.fieldKey] = false;
      else out[f.fieldKey] = '';
    }
    return out;
  };
  const onSubjectCustomChange = (fieldKey, value) => setSubjectCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  const onEntryCustomChange = (fieldKey, value) => setEntryCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  const onEditEntryCustomChange = (fieldKey, value) => setEditEntryCustomValues((prev) => ({ ...prev, [fieldKey]: value }));

  const validateRequiredCustomFields = (customFields, values) => {
    for (const f of customFields || []) {
      if (!f.required || f.hidden) continue;
      const v = values[f.fieldKey];
      if (v === undefined || v === null || v === '') {
        return `${f.label || f.fieldKey} is required.`;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!showEditModal || !editSubjectFormConfig.customFieldsEnabled || !chainId) return undefined;
    let cancelled = false;
    setSubjectCustomValues(defaultsFromCustomFields(editSubjectFormConfig.customFields));
    adminAPI.getCustomFieldValues('CorrespondenceChain', chainId)
      .then((res) => {
        if (cancelled) return;
        setSubjectCustomValues((prev) => ({ ...prev, ...(res?.data?.data?.values || res?.data?.values || {}) }));
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditModal, editSubjectFormConfig.customFieldsEnabled, chainId]);

  useEffect(() => {
    if (!showAppendModal) return;
    setEntryCustomValues(defaultsFromCustomFields(appendEntryFormConfig.customFields));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAppendModal, appendEntryFormConfig.customFields]);

  useEffect(() => {
    if (!showEditEntryModal || !editEntryFormConfig.customFieldsEnabled || !editEntryTarget?.id) return undefined;
    let cancelled = false;
    setEditEntryCustomValues(defaultsFromCustomFields(editEntryFormConfig.customFields));
    adminAPI.getCustomFieldValues('CorrespondenceEntry', editEntryTarget.id)
      .then((res) => {
        if (cancelled) return;
        setEditEntryCustomValues((prev) => ({ ...prev, ...(res?.data?.data?.values || res?.data?.values || {}) }));
      })
      .catch(() => { /* keep defaults */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditEntryModal, editEntryFormConfig.customFieldsEnabled, editEntryTarget?.id]);

  const normalizeApiChain = (chain) => ({
    ...chain,
    type: chain.type || 'Query',
    severity: chain.severity || 'Routine',
    entry_count: chain.entry_count ?? 0,
  });

  const loadThreadFromApi = useCallback(async () => {
    if (!chainId) return;
    setIsLoadingData(true);
    try {
      const [chainResp, entriesResp] = await Promise.all([
        correspondenceAPI.getChainById(chainId),
        correspondenceAPI.getEntries(chainId),
      ]);
      setApiChain(normalizeApiChain(chainResp?.data || null));
      setApiEntries(safeArray(entriesResp?.data));
      setUseApiData(true);
    } catch (error) {
      console.warn('Thread API not reachable, using local fallback:', error?.message || error);
      setUseApiData(false);
    } finally {
      setIsLoadingData(false);
    }
  }, [chainId]);

  useEffect(() => {
    loadThreadFromApi();
  }, [loadThreadFromApi]);

  useEffect(() => {
    const linkedType = apiChain?.linked_entity_type;
    const linkedId = apiChain?.linked_entity_id;
    if (!linkedType || linkedType !== 'SUBMITTAL' || !linkedId) {
      setLinkedSubmittal(null);
      return undefined;
    }
    let cancelled = false;
    submittalsAPI.getById(linkedId)
      .then((resp) => { if (!cancelled) setLinkedSubmittal(resp?.data || null); })
      .catch(() => { if (!cancelled) setLinkedSubmittal(null); });
    return () => { cancelled = true; };
  }, [apiChain?.linked_entity_type, apiChain?.linked_entity_id]);

  const chain = useApiData ? apiChain : null;
  const entries = useApiData
    ? [...apiEntries].sort((a, b) => new Date(b.letter_date || 0) - new Date(a.letter_date || 0))
    : [];
  const focusAppliedRef = useRef(false);

  useEffect(() => {
    focusAppliedRef.current = false;
  }, [chainId]);

  useEffect(() => {
    if (!focusEntryId || isLoadingData || !chain || Number(chain.id) !== Number(chainId)) return;
    const idNum = Number(focusEntryId);
    if (!Number.isFinite(idNum) || idNum <= 0) {
      onFocusEntryConsumed?.();
      return;
    }
    const match = entries.some((e) => Number(e.id) === idNum);
    if (!match) {
      onFocusEntryConsumed?.();
      return;
    }
    if (focusAppliedRef.current) return;
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(`cc-thread-entry-${idNum}`);
      if (el) {
        setActiveDetailTab('timeline');
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.45)';
        setTimeout(() => { el.style.boxShadow = ''; }, 3000);
      }
      focusAppliedRef.current = true;
      onFocusEntryConsumed?.();
    });
    return () => cancelAnimationFrame(raf);
  }, [focusEntryId, isLoadingData, chain, chainId, entries, onFocusEntryConsumed]);

  const projectSource = projectOptions || [];
  const project = chain ? projectSource.find(p => p.id === chain.project_id) : null;
  const briefDesignations = useMemo(
    () => (project?.brief_designations || []).filter((d) => d && String(d).trim()),
    [project]
  );

  if (isLoadingData) {
    return (
      <div className="loading-state">
        <p>Loading thread...</p>
      </div>
    );
  }

  if (!chain) {
    return (
      <div className="empty-state">
        <h3>Thread Not Found</h3>
        <p>The correspondence thread could not be found.</p>
        <button className="btn-primary" onClick={onBack}>Back to Register</button>
      </div>
    );
  }

  const statusHistory = [
    { old_status: '-', new_status: 'Open', changed_by: 'Rajesh Kumar', changed_at: chain.created_at, remarks: 'Correspondence Subject created' },
    ...(chain.status === 'Closed' ? [{ old_status: 'Open', new_status: 'Closed', changed_by: 'Priya Sharma', changed_at: chain.last_entry_date, remarks: 'Resolution received' }] : []),
  ];

  const linkedEntities = [
    ...(chain?.linkedEntities || []),
    ...(linkedSubmittal ? [{
      entity: 'Submittal',
      ref: linkedSubmittal.submittal_no || linkedSubmittal.base_number || `#${linkedSubmittal.id}`,
      status: formatSubmittalStatus(linkedSubmittal.status),
      value: null,
      submittalId: linkedSubmittal.id,
    }] : []),
  ];

  const formatFileSize = (size) => {
    if (!size && size !== 0) return '-';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const buildAttachmentFallbackText = (entry, file) => {
    const entryRef = entry?.ref_no || '-';
    const entryDirection = entry?.direction || file?.direction || '-';
    const entryDate = entry?.letter_date || file?.letter_date || '-';
    return [
      'Construction Correspondence Attachment Record',
      '-------------------------------------------',
      `Correspondence Subject: ${chain.subject}`,
      `Reference: ${entryRef}`,
      `Direction: ${entryDirection}`,
      `Date: ${formatDateIN(entryDate)}`,
      `File Name: ${file.name}`,
      `File Type: ${file.type || 'application/octet-stream'}`,
      `File Size: ${formatFileSize(file.size)}`,
      `Uploaded At: ${formatDateIN(file.uploaded_at)}`,
      '',
      'Note: Demo system stores file metadata only.',
    ].join('\n');
  };

  const handleDownloadAttachment = (entry, file) => {
    downloadAttachment(file, buildAttachmentFallbackText(entry, file));
  };

  const handleViewAttachment = (entry, file) => {
    if (previewBlobUrl) {
      try { URL.revokeObjectURL(previewBlobUrl); } catch (_) {}
      setPreviewBlobUrl(null);
    }
    const blob = file?.data_url ? dataUrlToBlobUrl(file.data_url) : null;
    setPreviewBlobUrl(blob || null);
    setSelectedAttachment({ entry, file });
  };

  const handleDownloadChainAttachments = async () => {
    setDownloadingAttachments(true);
    try {
      const ok = await downloadAttachmentsZip({
        chains: [chain],
        entries,
        zipName: `${chain.subject}_attachments`,
      });
      if (!ok) alert('No attachments found in this correspondence.');
    } finally {
      setDownloadingAttachments(false);
    }
  };

  const allAttachments = entries.flatMap((entry) => (entry.attachments || []).map((file) => ({
    ...file,
    ref_no: entry.ref_no,
    letter_date: entry.letter_date,
    direction: entry.direction,
    entry_id: entry.id,
  })));
  const entryCount = entries.length;
  const latestActivityDate = entries.reduce((latest, entry) => {
    const candidate = String(entry.letter_date || entry.entry_date || '');
    if (!candidate) return latest;
    if (!latest) return candidate;
    return candidate > latest ? candidate : latest;
  }, '');
  const handleAppendAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewEntry((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const handleRemoveAppendAttachment = (index) => {
    const next = [...(newEntry.attachments || [])];
    next.splice(index, 1);
    setNewEntry({ ...newEntry, attachments: next });
  };

  const openEditEntryModal = (entry) => {
    setEditEntryTarget(entry);
    setEditEntryForm({
      ref_no: entry.ref_no || '',
      direction: entry.direction || 'Outward',
      from: entry.from || '',
      to: entry.to || '',
      letter_date: entry.letter_date ? entry.letter_date.slice(0, 10) : '',
      entry_date: entry.entry_date ? entry.entry_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      mode: normalizeMode(entry.mode),
      summary: entry.summary || '',
      attachments: [...(entry.attachments || [])],
    });
    setEditEntryError('');
    setShowEditEntryModal(true);
  };

  const handleEditEntryAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const { accepted, rejected } = partitionFilesByMaxSize(files);
    if (rejected.length) {
      alert(correspondenceOversizeMessage(rejected));
    }
    if (!accepted.length) {
      event.target.value = '';
      return;
    }
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(accepted);
      setEditEntryForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const handleRemoveEditEntryAttachment = (index) => {
    setEditEntryForm((prev) => {
      const next = [...(prev.attachments || [])];
      next.splice(index, 1);
      return { ...prev, attachments: next };
    });
  };

  const handleUpdateEntry = async () => {
    if (!editEntryTarget) return;
    if (!isEmailCorrespondenceMode(editEntryForm.mode) && !editEntryForm.ref_no?.trim()) {
      setEditEntryError('Letter reference number is required.');
      return;
    }
    if (!editEntryForm.from?.trim() || !editEntryForm.to?.trim() || !editEntryForm.summary?.trim()) {
      setEditEntryError('From, To and Summary are required.');
      return;
    }
    if (!editEntryForm.letter_date) {
      setEditEntryError('Letter/Email date is required.');
      return;
    }
    if (!editEntryForm.attachments?.length) {
      setEditEntryError('At least one attachment is required.');
      return;
    }
    if (editEntryFormConfig.customFieldsEnabled) {
      const customFieldError = validateRequiredCustomFields(editEntryFormConfig.customFields, editEntryCustomValues);
      if (customFieldError) {
        setEditEntryError(customFieldError);
        return;
      }
    }
    if (!(await confirmDialog(`Update this ${editEntryForm.direction} entry?`, 'Update Entry'))) return;

    const fromVal = (briefDesignations.length > 0 && editEntryForm.direction === 'Outward') ? DEC_INFRA : editEntryForm.from.trim();
    const toVal = (briefDesignations.length > 0 && editEntryForm.direction === 'Inward') ? DEC_INFRA : editEntryForm.to.trim();

    try {
      await correspondenceAPI.updateEntry(chainId, editEntryTarget.id, {
        ref_no: editEntryForm.ref_no.trim() || null,
        direction: editEntryForm.direction,
        from: fromVal,
        to: toVal,
        letter_date: editEntryForm.letter_date,
        entry_date: editEntryForm.entry_date || new Date().toISOString().slice(0, 10),
        mode: editEntryForm.mode,
        summary: editEntryForm.summary.trim(),
        attachments: editEntryForm.attachments || [],
      });
      if (editEntryFormConfig.customFieldsEnabled) {
        try {
          await adminAPI.saveCustomFieldValues('CorrespondenceEntry', editEntryTarget.id, {
            pageKey: 'thread_detail',
            projectId: chain?.project_id,
            values: editEntryCustomValues,
            mode: 'edit',
          });
        } catch (cvErr) {
          console.error('Custom field save failed:', cvErr);
          notifyError(cvErr?.response?.data?.message || 'Entry updated but custom fields failed to save.');
        }
      }
      await loadThreadFromApi();
      setShowEditEntryModal(false);
      setEditEntryTarget(null);
      notifySuccess('Entry updated successfully.');
    } catch (error) {
      console.error('Failed to update entry:', error);
      setEditEntryError('Unable to update entry. Please retry.');
      notifyError('Unable to update entry. Please retry.');
    }
  };

  const handleDeleteEntry = async (entry) => {
    const dirLabel = entry.direction === 'Inward' ? 'Inward' : 'Outward';
    if (!(await confirmDialog(`Delete this ${dirLabel} entry (${entry.ref_no})? This cannot be undone.`, 'Delete Entry'))) return;
    try {
      await correspondenceAPI.deleteEntry(chainId, entry.id);
      await loadThreadFromApi();
      notifySuccess('Entry deleted.');
    } catch (error) {
      console.error('Failed to delete entry:', error);
      const msg = error?.response?.data?.message || error?.message || 'Unknown error';
      notifyError(`Unable to delete entry: ${msg}`);
    }
  };

  const handleAppendEntry = async () => {
    if (!isEmailCorrespondenceMode(newEntry.mode) && !newEntry.ref_no?.trim()) {
      setEntryError('Letter reference number is required.');
      return;
    }
    if (!newEntry.from.trim() || !newEntry.to.trim() || !newEntry.summary.trim()) {
      setEntryError('From, To and Summary are required.');
      return;
    }
    if (!newEntry.letter_date) {
      setEntryError('Letter/Email date is required.');
      return;
    }
    if (!newEntry.attachments || newEntry.attachments.length === 0) {
      setEntryError('At least one attachment is required.');
      return;
    }
    if (appendEntryFormConfig.customFieldsEnabled) {
      const customFieldError = validateRequiredCustomFields(appendEntryFormConfig.customFields, entryCustomValues);
      if (customFieldError) {
        setEntryError(customFieldError);
        return;
      }
    }
    const directionLabel = newEntry.direction === 'Inward' ? 'Inward' : 'Outward';
    if (!(await confirmDialog(`Add this ${directionLabel} entry to this Correspondence Subject?`, 'Add Entry'))) return;

    if (useApiData) {
      const appendViaApi = async () => {
        try {
          const entryDate = new Date().toISOString().slice(0, 10);
          const letterDate = newEntry.letter_date;
          const dueDate = newEntry.response_expected
            ? new Date(new Date(letterDate).getTime() + (Number(newEntry.sla_days) || 0) * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
            : null;

          const entryResp = await correspondenceAPI.createEntry(chainId, {
            ref_no: newEntry.ref_no.trim() || null,
            direction: newEntry.direction,
            from: newEntry.from.trim(),
            to: newEntry.to.trim(),
            letter_date: letterDate,
            entry_date: entryDate,
            summary: newEntry.summary.trim(),
            response_expected: newEntry.response_expected,
            sla_days: Number(newEntry.sla_days) || 0,
            due_date: dueDate,
            mode: newEntry.mode,
            attachments: newEntry.attachments || [],
            priority: normalizePriority(newEntry.priority, normalizePriority(chain.severity)),
            severity: normalizePriority(newEntry.priority, normalizePriority(chain.severity)),
          });

          const createdEntry = entryResp?.data;
          if (appendEntryFormConfig.customFieldsEnabled && createdEntry?.id) {
            try {
              await adminAPI.saveCustomFieldValues('CorrespondenceEntry', createdEntry.id, {
                pageKey: 'thread_detail',
                projectId: chain.project_id,
                values: entryCustomValues,
                mode: 'create',
              });
            } catch (cvErr) {
              console.error('Custom field save failed:', cvErr);
              notifyError(cvErr?.response?.data?.message || 'Entry added but custom fields failed to save.');
            }
          }

          await loadThreadFromApi();
          setEntryError('');
          setShowAppendModal(false);
          setNewEntry({
            direction: 'Outward', ref_no: '', from: DEC_INFRA, to: '', summary: '',
            sla_days: 7, response_expected: true, mode: 'Letter', priority: normalizePriority(chain.severity), letter_date: '', attachments: []
          });
          setEntryCustomValues({});
          notifySuccess('Entry added successfully.');
        } catch (error) {
          console.error('Failed to append entry via API:', error);
          setEntryError('Unable to append entry via API. Please retry.');
          notifyError('Unable to append entry. Please retry.');
        }
      };
      appendViaApi();
      return;
    }

    setEntryError('API required to append entry. Please ensure the API is available.');
  };

  const handleCloseChain = async () => {
    if (!(await confirmDialog('Close this Correspondence Subject?', 'Close Correspondence Subject'))) return;
    try {
      await correspondenceAPI.updateChain(chainId, { status: 'Closed' });
      await loadThreadFromApi();
    } catch (error) {
      console.error('Failed to close chain:', error);
      alert('Unable to close this Correspondence Subject.');
    }
  };

  const handleReopenChain = async () => {
    if (!(await confirmDialog('Reopen this Correspondence Subject?', 'Reopen Correspondence Subject'))) return;
    try {
      await correspondenceAPI.updateChain(chainId, { status: 'Open' });
      await loadThreadFromApi();
    } catch (error) {
      console.error('Failed to reopen chain:', error);
      alert('Unable to reopen this Correspondence Subject.');
    }
  };

  const handleDeleteChain = async () => {
    if (!(await confirmDialog('Delete this Correspondence Subject permanently? All entries will be removed.', 'Delete Correspondence'))) return;
    try {
      await correspondenceAPI.deleteChain(chainId);
      notifySuccess('Correspondence deleted.');
      onBack();
    } catch (error) {
      console.error('Failed to delete chain:', error);
      notifyError('Unable to delete correspondence. Admin access may be required.');
    }
  };

  const mapLinkedEntityToCategoryData = (entity, linkedType) => {
    if (!entity) return {};
    const d = entity.data || {};
    switch (linkedType) {
      case 'RA_BILL':
        return {
          ra_no: d.ra_no ?? entity.ra_no,
          ra_ref: entity.billNo || d.ra_ref,
          period_from: d.period_from || null,
          period_to: d.period_to || null,
          gross_work_done: d.gross_work_done ?? 0,
          deductions_total: d.deductions_total ?? 0,
          net_payable: entity.amount ?? d.net_payable ?? 0,
          paid_value: d.paid_value ?? 0,
          ra_status: entity.status || d.ra_status,
          payment_status: d.payment_status || 'Unpaid',
          payment_due_date: d.payment_due_date || null,
          ra_approved_quantities: d.ra_approved_quantities ?? null,
        };
      case 'BANK_GUARANTEE':
        return {
          bg_type: d.bg_type || 'Security Bank Guarantee',
          bg_purpose: d.purpose || d.bg_purpose || '',
          bg_number: entity.bgNo || d.bg_number || '',
          bg_bank_name: d.bank || d.bg_bank_name || '',
          bg_ifsc: d.ifsc || d.bg_ifsc || '',
          bg_branch: d.branch || d.bg_branch || '',
          bg_amount: entity.amount ?? d.bg_amount ?? 0,
          bg_issue_date: d.issue_date || d.bg_issue_date || null,
          bg_expiry_date: d.expiry_date || d.bg_expiry_date || null,
          bg_claim_expiry_date: d.claim_expiry_date || d.bg_claim_expiry_date || null,
          bg_status: entity.status || d.bg_status || 'Active',
        };
      case 'CLEARANCE':
        return {
          clearance_type: d.clearance_type || entity.title || '',
          authority: d.authority || '',
          submission_date: d.submission_date || null,
          target_date: d.target_date || null,
          clearance_status: entity.status || d.clearance_status || 'Submitted',
          clearance_remarks: d.remarks || d.clearance_remarks || '',
        };
      case 'MILESTONE':
        return {
          request_no: d.request_no || entity.title || '',
          milestone_id: d.milestone_id ?? null,
          eot_days_requested: d.eot_days_requested ?? 0,
          requested_completion_date: entity.dueDate ? new Date(entity.dueDate).toISOString().slice(0, 10) : (d.requested_completion_date || null),
          reason_category: d.reason_category || '',
          financial_impact_applicable: d.financial_impact_applicable || 'No',
          estimated_cost_impact: d.estimated_cost_impact ?? 0,
          ld_risk_impact: d.ld_risk_impact ?? 0,
          financial_remarks: d.financial_remarks || '',
          shift_status: entity.status || d.shift_status || 'Submitted',
          eot_reason: d.remarks || d.eot_reason || '',
        };
      case 'SPECIALIZED_ITEM':
      case 'DEVIATION':
      case 'SCOPE_CHANGE':
      case 'CLAIM': {
        const base = { ...d };
        if (linkedType === 'DEVIATION') base.deviation_description = entity.title || d.deviation_description;
        if (linkedType === 'SCOPE_CHANGE') base.scope_summary = entity.title || d.scope_summary;
        if (linkedType === 'CLAIM') base.claim_ref = entity.title || d.claim_ref;
        if (linkedType === 'SPECIALIZED_ITEM') base.item_name = entity.title || d.item_name;
        return base;
      }
      case 'QAQC':
        return {
          checklist_no: d.checklist_no || '',
          qaqc_category: d.category || d.qaqc_category || '',
          qaqc_result: entity.result || d.qaqc_result || 'Conditional',
          qaqc_status: entity.status || d.qaqc_status || 'Under Review',
          inspection_date: d.inspection_date || null,
          inspector_name: d.inspector || d.inspector_name || '',
          parameters_checked: d.parameters_checked || '',
          defects_found: d.defects_found || '',
          corrective_action: d.corrective_action || '',
          retest_date: d.retest_date || null,
          qaqc_remarks: d.remarks || d.qaqc_remarks || '',
        };
      default:
        return {};
    }
  };

  const openEditModal = async () => {
    const baseForm = {
      subject: chain.subject || '',
      category: normalizeCategoryForForm(chain.category || 'General'),
      location_block: chain.location_block || '',
      severity: normalizePriority(chain.severity),
      targeted_reply_days: chain.targeted_reply_days ?? 7,
      status: chain.status || 'Open',
      project_id: chain.project_id ? String(chain.project_id) : '',
      last_entry_date: chain.last_entry_date ? chain.last_entry_date.slice(0, 10) : '',
      category_data: {},
    };
    setEditForm(baseForm);
    setShowEditModal(true);
    const linkedType = chain.linked_entity_type;
    const linkedId = chain.linked_entity_id;
    if (!linkedType || !linkedId) {
      return;
    }
    setEditFormLoadingLinked(true);
    try {
      let entity = null;
      if (linkedType === 'RA_BILL') entity = (await raBillsAPI.getById(linkedId))?.data;
      else if (linkedType === 'BANK_GUARANTEE') entity = (await bankGuaranteesAPI.getById(linkedId))?.data;
      else if (linkedType === 'MILESTONE') entity = (await milestonesAPI.getById(linkedId))?.data;
      else if (linkedType === 'CLEARANCE') entity = (await clearancesAPI.getById(linkedId))?.data;
      else if (['SPECIALIZED_ITEM', 'DEVIATION', 'SCOPE_CHANGE', 'CLAIM'].includes(linkedType)) entity = (await deviationsAPI.getById(linkedId))?.data;
      else if (linkedType === 'QAQC') entity = (await qaqcAPI.getById(linkedId))?.data;
      const categoryData = mapLinkedEntityToCategoryData(entity, linkedType);
      setEditForm((prev) => ({ ...prev, category_data: categoryData }));
    } catch (err) {
      console.warn('Could not load linked entity for edit:', err);
    } finally {
      setEditFormLoadingLinked(false);
    }
  };

  const handleEditCategoryChange = (category) => {
    setEditForm((prev) => ({ ...prev, category, category_data: {} }));
  };

  const handleEditCategoryFieldChange = (field, value, type) => {
    const parsedValue = type === 'number' ? (value === '' ? '' : Number(value)) : value;
    setEditForm((prev) => ({
      ...prev,
      category_data: {
        ...(prev.category_data || {}),
        [field]: parsedValue,
      },
    }));
  };

  const getLinkedEntityTypeForCategory = (category) => {
    if (matchesRaBillCategory(category)) return 'RA_BILL';
    if (isBankGuaranteeCategory(category)) return 'BANK_GUARANTEE';
    if (matchesClearanceCategory(category)) return 'CLEARANCE';
    if (matchesMilestoneEotCategory(category)) return 'MILESTONE';
    if (category === 'Specialized Items') return 'SPECIALIZED_ITEM';
    if (matchesDeviationsExtraItemsCategory(category)) return 'DEVIATION';
    if (matchesScopeAgreementCategory(category)) return 'SCOPE_CHANGE';
    if (category === 'Contractual / Claims') return 'CLAIM';
    if (matchesQualityCategory(category)) return 'QAQC';
    return null;
  };

  const handleSaveEdit = async () => {
    if (!editForm.subject?.trim()) {
      notifyError('Subject is required.');
      return;
    }
    if (!editForm.project_id) {
      notifyError('Project is required.');
      return;
    }
    if (editSubjectFormConfig.customFieldsEnabled) {
      const customFieldError = validateRequiredCustomFields(editSubjectFormConfig.customFields, subjectCustomValues);
      if (customFieldError) {
        notifyError(customFieldError);
        return;
      }
    }
    const categoryFields = getCategoryFieldConfig(editForm.category);
    const catData = editForm.category_data || {};
    const hasLinkedEntity = !!getLinkedEntityTypeForCategory(editForm.category);
    if (hasLinkedEntity) {
      for (const field of categoryFields) {
        if (field.required) {
          const val = catData[field.key];
          if (val === undefined || val === null || val === '') {
            notifyError(`${field.label} is required for this category.`);
            return;
          }
        }
      }
    }
    try {
      const projectId = editForm.project_id ? parseInt(editForm.project_id, 10) : (chain.project_id ? parseInt(chain.project_id, 10) : null);
      if (!projectId) {
        notifyError('Project is required.');
        return;
      }
      let linkedEntityType = null;
      let linkedEntityId = null;
      const existingLinkedType = chain.linked_entity_type;
      const existingLinkedId = chain.linked_entity_id;

      if (matchesRaBillCategory(editForm.category)) {
        const payload = {
          projectId,
          billNo: String(catData.ra_ref || `RA-${Date.now().toString().slice(-5)}`),
          status: String(catData.ra_status || 'Submitted'),
          amount: Number(catData.net_payable || 0),
          data: {
            ra_no: Number(catData.ra_no || 0),
            period_from: catData.period_from || null,
            period_to: catData.period_to || null,
            gross_work_done: Number(catData.gross_work_done || 0),
            deductions_total: Number(catData.deductions_total || 0),
            net_payable: Number(catData.net_payable || 0),
            payment_status: catData.payment_status || 'Unpaid',
            payment_due_date: catData.payment_due_date || null,
            linked_correspondence: chainId,
          },
        };
        if (existingLinkedType === 'RA_BILL' && existingLinkedId) {
          await raBillsAPI.update(existingLinkedId, payload);
          linkedEntityType = 'RA_BILL';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await raBillsAPI.create(payload);
          linkedEntityType = 'RA_BILL';
          linkedEntityId = resp?.data?.id ?? null;
        }
      } else if (isBankGuaranteeCategory(editForm.category)) {
        const payload = {
          projectId,
          bgNo: String(catData.bg_number || `BG-${Date.now().toString().slice(-5)}`),
          status: String(catData.bg_status || 'Active'),
          amount: Number(catData.bg_amount || 0),
          data: {
            bg_type: String(catData.bg_type || 'Security BG'),
            purpose: String(catData.bg_purpose || ''),
            bank: String(catData.bg_bank_name || ''),
            ifsc: String(catData.bg_ifsc || ''),
            branch: String(catData.bg_branch || ''),
            issue_date: catData.bg_issue_date || null,
            expiry_date: catData.bg_expiry_date || null,
            claim_expiry_date: catData.bg_claim_expiry_date || null,
            linked_correspondence: chainId,
          },
        };
        if (existingLinkedType === 'BANK_GUARANTEE' && existingLinkedId) {
          await bankGuaranteesAPI.update(existingLinkedId, payload);
          linkedEntityType = 'BANK_GUARANTEE';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await bankGuaranteesAPI.create(payload);
          linkedEntityType = 'BANK_GUARANTEE';
          linkedEntityId = resp?.data?.id ?? null;
        }
      } else if (matchesClearanceCategory(editForm.category)) {
        const project = projectOptions.find((p) => p.id === projectId);
        const payload = {
          projectId,
          title: String(catData.clearance_type || editForm.subject || '').trim(),
          status: String(catData.clearance_status || 'Submitted'),
          data: {
            clearance_ref: `${project?.agreement_prefix || 'GEN'}/CLR/${Date.now().toString().slice(-6)}`,
            clearance_type: String(catData.clearance_type || '').trim(),
            authority: String(catData.authority || '').trim(),
            submission_date: catData.submission_date || null,
            target_date: catData.target_date || null,
            remarks: String(catData.clearance_remarks || editForm.subject || '').trim(),
            linked_correspondence: chainId,
          },
        };
        if (existingLinkedType === 'CLEARANCE' && existingLinkedId) {
          await clearancesAPI.update(existingLinkedId, payload);
          linkedEntityType = 'CLEARANCE';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await clearancesAPI.create(payload);
          linkedEntityType = 'CLEARANCE';
          linkedEntityId = resp?.data?.id ?? null;
        }
      } else if (matchesMilestoneEotCategory(editForm.category)) {
        const payload = {
          projectId,
          title: String(catData.request_no || editForm.subject || `EOT-${Date.now().toString().slice(-5)}`),
          status: String(catData.shift_status || 'Submitted'),
          dueDate: catData.requested_completion_date ? new Date(catData.requested_completion_date).toISOString() : null,
          data: {
            request_no: String(catData.request_no || ''),
            milestone_id: Number(catData.milestone_id || 0),
            eot_days_requested: Number(catData.eot_days_requested || 0),
            requested_completion_date: catData.requested_completion_date || null,
            reason_category: String(catData.reason_category || ''),
            financial_impact_applicable: String(catData.financial_impact_applicable || 'No'),
            estimated_cost_impact: Number(catData.estimated_cost_impact || 0),
            ld_risk_impact: Number(catData.ld_risk_impact || 0),
            financial_remarks: String(catData.financial_remarks || ''),
            remarks: String(catData.eot_reason || ''),
            linked_correspondence: chainId,
          },
        };
        if (existingLinkedType === 'MILESTONE' && existingLinkedId) {
          await milestonesAPI.update(existingLinkedId, payload);
          linkedEntityType = 'MILESTONE';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await milestonesAPI.create(payload);
          linkedEntityType = 'MILESTONE';
          linkedEntityId = resp?.data?.id ?? null;
        }
      } else if (editForm.category === 'Specialized Items') {
        const payload = {
          projectId,
          title: String(catData.item_name || editForm.subject || `SPC-${Date.now().toString().slice(-5)}`),
          category: 'Specialized Item',
          status: 'Submitted',
          data: {
            item_name: String(catData.item_name || '').trim(),
            spec_ref: String(catData.spec_ref || '').trim(),
            vendor_name: String(catData.vendor_name || '').trim(),
            country_of_origin: String(catData.country_of_origin || '').trim(),
            lead_time_days: Number(catData.lead_time_days || 0),
            expected_delivery_date: catData.expected_delivery_date || null,
            fat_required: String(catData.fat_required || 'No'),
            warranty_months: Number(catData.warranty_months || 0),
            special_instructions: String(catData.special_instructions || ''),
            linked_correspondence: chainId,
          },
        };
        if (existingLinkedType === 'SPECIALIZED_ITEM' && existingLinkedId) {
          await deviationsAPI.update(existingLinkedId, payload);
          linkedEntityType = 'SPECIALIZED_ITEM';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await deviationsAPI.create(payload);
          linkedEntityType = 'SPECIALIZED_ITEM';
          linkedEntityId = resp?.data?.id ?? null;
        }
      } else if (matchesDeviationsExtraItemsCategory(editForm.category)) {
        const payload = {
          projectId,
          title: String(catData.deviation_description || catData.extra_item_description || editForm.subject),
          category: 'Deviation',
          status: String(catData.deviation_status || 'Submitted'),
          data: {
            deviation_no: String(catData.deviation_no || `DEV-${Date.now().toString().slice(-5)}`),
            boq_item: String(catData.boq_item_ref || ''),
            original_qty: Number(catData.original_qty || 0),
            revised_qty: Number(catData.revised_qty || catData.extra_item_qty || 0),
            unit: String(catData.unit || ''),
            original_rate: Number(catData.original_rate || 0),
            revised_rate: Number(catData.revised_rate || catData.extra_item_rate || 0),
            linked_correspondence: chainId,
            remarks: String(catData.deviation_remarks || ''),
          },
        };
        if (existingLinkedType === 'DEVIATION' && existingLinkedId) {
          await deviationsAPI.update(existingLinkedId, payload);
          linkedEntityType = 'DEVIATION';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await deviationsAPI.create(payload);
          linkedEntityType = 'DEVIATION';
          linkedEntityId = resp?.data?.id ?? null;
        }
      } else if (matchesScopeAgreementCategory(editForm.category)) {
        const payload = {
          projectId,
          title: String(catData.scope_summary || editForm.subject),
          category: 'Scope',
          status: 'Submitted',
          data: {
            change_order_ref: String(catData.change_order_ref || ''),
            scope_summary: String(catData.scope_summary || ''),
            scope_value_impact: Number(catData.scope_value_impact || 0),
            approval_stage: String(catData.approval_stage || ''),
            linked_correspondence: chainId,
          },
        };
        if (existingLinkedType === 'SCOPE_CHANGE' && existingLinkedId) {
          await deviationsAPI.update(existingLinkedId, payload);
          linkedEntityType = 'SCOPE_CHANGE';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await deviationsAPI.create(payload);
          linkedEntityType = 'SCOPE_CHANGE';
          linkedEntityId = resp?.data?.id ?? null;
        }
      } else if (editForm.category === 'Contractual / Claims') {
        const payload = {
          projectId,
          title: String(catData.claim_ref || editForm.subject),
          category: 'Claim',
          status: 'Submitted',
          data: {
            claim_ref: String(catData.claim_ref || ''),
            claim_amount: Number(catData.claim_amount || 0),
            claim_basis: String(catData.claim_basis || ''),
            claim_notice_date: catData.claim_notice_date || null,
            linked_correspondence: chainId,
          },
        };
        if (existingLinkedType === 'CLAIM' && existingLinkedId) {
          await deviationsAPI.update(existingLinkedId, payload);
          linkedEntityType = 'CLAIM';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await deviationsAPI.create(payload);
          linkedEntityType = 'CLAIM';
          linkedEntityId = resp?.data?.id ?? null;
        }
      } else if (matchesQualityCategory(editForm.category)) {
        const payload = {
          projectId,
          activity: String(editForm.subject || ''),
          result: String(catData.qaqc_result || 'Conditional'),
          status: String(catData.qaqc_status || 'Under Review'),
          data: {
            checklist_no: String(catData.checklist_no || ''),
            category: String(catData.qaqc_category || ''),
            inspection_date: catData.inspection_date || null,
            inspector: String(catData.inspector_name || ''),
            parameters_checked: String(catData.parameters_checked || ''),
            defects_found: String(catData.defects_found || ''),
            corrective_action: String(catData.corrective_action || ''),
            retest_date: catData.retest_date || null,
            remarks: String(catData.qaqc_remarks || ''),
            linked_correspondence: chainId,
          },
        };
        if (existingLinkedType === 'QAQC' && existingLinkedId) {
          await qaqcAPI.update(existingLinkedId, payload);
          linkedEntityType = 'QAQC';
          linkedEntityId = existingLinkedId;
        } else {
          const resp = await qaqcAPI.create(payload);
          linkedEntityType = 'QAQC';
          linkedEntityId = resp?.data?.id ?? null;
        }
      }

      const updatePayload = {
        subject: editForm.subject.trim(),
        category: editForm.category,
        location_block: editForm.location_block?.trim() || null,
        severity: editForm.severity,
        targeted_reply_days: editForm.targeted_reply_days,
        status: editForm.status,
        ...(editForm.project_id && { project_id: parseInt(editForm.project_id, 10) }),
        ...(editForm.last_entry_date && { last_entry_date: editForm.last_entry_date }),
      };
      if (linkedEntityType && linkedEntityId) {
        updatePayload.linked_entity_type = linkedEntityType;
        updatePayload.linked_entity_id = linkedEntityId;
      } else if (hasLinkedEntity === false && (existingLinkedType || existingLinkedId)) {
        updatePayload.linked_entity_type = null;
        updatePayload.linked_entity_id = null;
      }
      await correspondenceAPI.updateChain(chainId, updatePayload);
      if (editSubjectFormConfig.customFieldsEnabled) {
        try {
          await adminAPI.saveCustomFieldValues('CorrespondenceChain', chainId, {
            pageKey: 'thread_detail',
            projectId,
            values: subjectCustomValues,
            mode: 'edit',
          });
        } catch (cvErr) {
          console.error('Custom field save failed:', cvErr);
          notifyError(cvErr?.response?.data?.message || 'Correspondence updated but custom fields failed to save.');
        }
      }
      await loadThreadFromApi();
      setShowEditModal(false);
      notifySuccess('Correspondence updated.');
    } catch (error) {
      console.error('Failed to update chain:', error);
      notifyError('Unable to update correspondence. Please retry.');
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <button className="btn-secondary btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          Back to Register
        </button>
      </div>

      <div style={{ background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ marginBottom: 8 }}>
              <ProjectNameText as="span">{project?.name || '—'}</ProjectNameText>
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 600, color: '#1f2937', margin: 0 }}>
              {chain.subject}
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary btn-sm"
            onClick={openEditModal}
          >
            Edit
          </button>
          {isAdmin && (
            <button
              className="btn-secondary btn-sm"
              style={{ borderColor: '#fecaca', color: '#b91c1c' }}
              onClick={handleDeleteChain}
            >
              Delete
            </button>
          )}
            <button
              className="btn-secondary btn-sm"
              onClick={() => setShowThreadReportPreview(true)}
            >
              View Report
            </button>
            {typeof onOpenDrawingsDesign === 'function' && isDrawingDesignCorrespondenceCategory(chain.category) && (
              <button type="button" className="btn-secondary btn-sm" onClick={onOpenDrawingsDesign}>
                Open Drawings &amp; Design
              </button>
            )}
            <button
              className="btn-secondary btn-sm"
              onClick={handleDownloadChainAttachments}
              disabled={downloadingAttachments}
            >
              {downloadingAttachments ? 'Preparing ZIP...' : 'Download Attachments'}
            </button>
            <StatusBadge tone={chain.status.toLowerCase()}>{chain.status}</StatusBadge>
            <StatusBadge tone={chain.severity.toLowerCase()}>{chain.severity}</StatusBadge>
            {chain.sla_breached && <StatusBadge tone="sla-breached">Targeted Reply Overdue</StatusBadge>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, fontSize: 13, color: '#6b7280', flexWrap: 'wrap' }}>
          <span>Category: <strong className="category-tag" style={{ fontSize: 12 }}>{displayCorrespondenceCategory(chain.category || 'General')}</strong></span>
          {chain.location_block ? (
            <span>Location / block: <strong style={{ color: '#374151' }}>{chain.location_block}</strong></span>
          ) : null}
          <span>Type: <strong style={{ color: '#374151' }}>{chain.type}</strong></span>
          <span>Entries: <strong style={{ color: '#374151' }}>{entryCount}</strong></span>
          <span>Created: <strong style={{ color: '#374151' }}>{formatDateIN(chain.created_at)}</strong></span>
          <span>Last letter date: <strong style={{ color: '#374151' }}>{formatDateIN(latestActivityDate || chain.last_entry_date)}</strong></span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button className={`filter-pill ${activeDetailTab === 'timeline' ? 'active' : ''}`} onClick={() => setActiveDetailTab('timeline')}>Timeline</button>
        <button className={`filter-pill ${activeDetailTab === 'linked' ? 'active' : ''}`} onClick={() => setActiveDetailTab('linked')}>Linked Entities</button>
        <button className={`filter-pill ${activeDetailTab === 'history' ? 'active' : ''}`} onClick={() => setActiveDetailTab('history')}>History</button>
        <button className={`filter-pill ${activeDetailTab === 'attachments' ? 'active' : ''}`} onClick={() => setActiveDetailTab('attachments')}>Attachments</button>
        <div style={{ flex: 1 }} />
        {chain.status === 'Open' ? (
          <>
            <button
              className="btn-secondary btn-sm"
              onClick={handleCloseChain}
            >
              Close Subject
            </button>
            <button
              className="btn-primary btn-sm"
              onClick={() => {
                setNewEntry(prev => ({ ...prev, priority: normalizePriority(chain.severity) }));
                setShowAppendModal(true);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Append Entry
            </button>
          </>
        ) : (
          <button
            className="btn-primary btn-sm"
            onClick={handleReopenChain}
          >
            Reopen Subject
          </button>
        )}
      </div>

      {activeDetailTab === 'timeline' && (
        <div>
          {entries.length === 0 ? (
            <div className="empty-state">
              <h3>No Entries Yet</h3>
              <p>Start the conversation by appending the first entry.</p>
            </div>
          ) : (
            entries.map((entry) => (
              <div
                key={entry.id}
                id={`cc-thread-entry-${entry.id}`}
                style={{
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  marginBottom: 10,
                  overflow: 'hidden',
                }}
              >
                <div style={{ padding: '12px 14px 14px 14px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 13,
                          fontWeight: 700,
                          color: entry.direction === 'Outward' ? '#059669' : '#4f46e5',
                          background: entry.direction === 'Outward' ? '#d1fae5' : '#e0e7ff',
                        }}
                      >
                        {entry.direction === 'Outward' ? '↗' : '↙'}
                      </span>
                      <StatusBadge tone={entry.direction === 'Outward' ? 'open' : 'active'}>{entry.direction}</StatusBadge>
                      <StatusBadge tone={normalizePriority(entry.priority, normalizePriority(chain.severity)).toLowerCase()}>
                        {normalizePriority(entry.priority, normalizePriority(chain.severity))}
                      </StatusBadge>
                      <strong style={{ color: '#1f2937', fontSize: 14 }}>{entry.ref_no}</strong>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{formatDateIN(entry.letter_date)}</span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        <button
                          className="btn-secondary btn-sm"
                          onClick={(e) => { e.stopPropagation(); openEditEntryModal(entry); }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-secondary btn-sm"
                          style={{ borderColor: '#fecaca', color: '#b91c1c' }}
                          onClick={(e) => { e.stopPropagation(); handleDeleteEntry(entry); }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>From: {entry.from}</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>To: {entry.to}</span>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>Attachments: {(entry.attachments || []).length}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', margin: '8px 0 8px 0' }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>Mode: {normalizeMode(entry.mode)}</span>
                    {entry.response_expected && entry.due_date && (
                      <span style={{ fontSize: 12, color: new Date(entry.due_date) < new Date() ? '#ef4444' : '#6b7280' }}>
                        Due: {formatDateIN(entry.due_date)}
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: 13, color: '#374151', margin: '0 0 10px 0' }}>{entry.summary}</p>
                  <div style={{ marginTop: 10, borderTop: '1px dashed #e5e7eb', paddingTop: 10 }}>
                    <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px 0' }}>Attachments</p>
                    {(entry.attachments || []).length === 0 ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 12,
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          padding: '8px 10px',
                        }}
                      >
                        <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>No attachments on this entry.</p>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button className="btn-secondary btn-sm" disabled>View</button>
                          <button className="btn-secondary btn-sm" disabled>Download</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {entry.attachments.map((file, index) => (
                          <div
                            key={`${entry.id}-${file.name}-${index}`}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 12,
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              borderRadius: 6,
                              padding: '8px 10px',
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <p style={{ margin: 0, fontSize: 12, color: '#111827', fontWeight: 500, wordBreak: 'break-word' }}>{file.name}</p>
                              <p style={{ margin: '2px 0 0 0', fontSize: 11, color: '#6b7280' }}>
                                {formatFileSize(file.size)} | {file.type || 'application/octet-stream'}
                              </p>
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                              <button
                                className="btn-secondary btn-sm"
                                onClick={() => handleViewAttachment(entry, file)}
                              >
                                View
                              </button>
                              <button
                                className="btn-secondary btn-sm"
                                onClick={() => handleDownloadAttachment(entry, file)}
                              >
                                Download
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeDetailTab === 'linked' && (
        linkedEntities.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <h3>No Linked Entities</h3>
            <p>No linked register record (RA/BG/Deviation/EOT/QAQC/Clearance/Submittal) is linked to this Correspondence Subject.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Reference</th>
                  <th>Status</th>
                  <th className="amount-cell">Value / Impact</th>
                </tr>
              </thead>
              <tbody>
                {linkedEntities.map((item, index) => (
                  <tr key={`${item.ref}-${index}`}>
                    <td>{item.entity}</td>
                    <td style={{ fontWeight: 500, color: '#7C3AED' }}>
                      {item.submittalId && typeof onViewSubmittal === 'function' ? (
                        <button type="button" className="btn-link" style={{ padding: 0, background: 'none', border: 'none', color: '#7C3AED', cursor: 'pointer', fontWeight: 500 }} onClick={() => onViewSubmittal(item.submittalId)}>{item.ref}</button>
                      ) : item.ref}
                    </td>
                    <td>{item.entity === 'Submittal' ? <SubmittalStatusBadge status={linkedSubmittal?.status} isOverdue={linkedSubmittal?.is_overdue} showOverdueFlag={false} /> : <StatusBadge tone="under-review">{item.status}</StatusBadge>}</td>
                    <td className="amount-cell">{item.value === null ? '-' : (item.entity.includes('EOT') ? `${item.value} days` : formatCurrency(item.value))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {activeDetailTab === 'history' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Old Status</th>
                <th>New Status</th>
                <th>Changed By</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {statusHistory.map((h, i) => (
                <tr key={i}>
                  <td>{h.changed_at}</td>
                  <td>{h.old_status}</td>
                  <td><StatusBadge tone={h.new_status.toLowerCase()}>{h.new_status}</StatusBadge></td>
                  <td>{h.changed_by}</td>
                  <td>{h.remarks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeDetailTab === 'attachments' && (
        allAttachments.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            <h3>No Attachments</h3>
            <p>Add attachments while creating or appending entries.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Entry Ref</th>
                  <th>Date</th>
                  <th>Direction</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allAttachments.map((file, index) => (
                  <tr key={`${file.ref_no}-${file.name}-${index}`}>
                    <td>{file.name}</td>
                    <td style={{ color: '#7C3AED', fontWeight: 500 }}>{file.ref_no}</td>
                    <td>{formatDateIN(file.letter_date)}</td>
                    <td>{file.direction}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => handleViewAttachment(entries.find(e => e.id === file.entry_id), file)}
                        >
                          View
                        </button>
                        <button
                          className="btn-secondary btn-sm"
                          onClick={() => handleDownloadAttachment(entries.find(e => e.id === file.entry_id), file)}
                        >
                          Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {showThreadReportPreview && (
        <ThreadReportPreviewModal
          chain={chain}
          project={project}
          entries={entries}
          onClose={() => setShowThreadReportPreview(false)}
        />
      )}

      {selectedAttachment && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Attachment Details</h2>
              <button className="btn-close" onClick={() => {
                if (previewBlobUrl) { try { URL.revokeObjectURL(previewBlobUrl); } catch (_) {} setPreviewBlobUrl(null); }
                setSelectedAttachment(null);
              }}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>File Name</p><p style={{ fontSize: 14 }}>{selectedAttachment.file.name}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Size</p><p style={{ fontSize: 14 }}>{formatFileSize(selectedAttachment.file.size)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Type</p><p style={{ fontSize: 14 }}>{selectedAttachment.file.type || 'application/octet-stream'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Uploaded At</p><p style={{ fontSize: 14 }}>{selectedAttachment.file.uploaded_at || '-'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Entry Ref</p><p style={{ fontSize: 14 }}>{selectedAttachment.entry?.ref_no || '-'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Direction</p><p style={{ fontSize: 14 }}>{selectedAttachment.entry?.direction || '-'}</p></div>
              </div>
              {(selectedAttachment.file?.data_url || previewBlobUrl) && (
                <div style={{ marginTop: 16, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', maxHeight: 400 }}>
                  {/^data:image\//i.test(selectedAttachment.file.data_url || '') ? (
                    <img src={previewBlobUrl || selectedAttachment.file.data_url} alt={selectedAttachment.file.name} style={{ maxWidth: '100%', display: 'block' }} />
                  ) : /pdf/i.test(selectedAttachment.file.type || '') ? (
                    previewBlobUrl ? (
                      <iframe src={previewBlobUrl} title={selectedAttachment.file.name} style={{ width: '100%', height: 380, border: 'none' }} />
                    ) : (
                      <p style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>Use &quot;Open in New Tab&quot; or Download to view this PDF.</p>
                    )
                  ) : (
                    <p style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>Preview not available for this file type. Use &quot;Open in New Tab&quot; or Download.</p>
                  )}
                </div>
              )}
              {!selectedAttachment.file?.data_url && (
                <p style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
                  This older record has metadata only. Download will generate an attachment record file.
                </p>
              )}
              <div className="modal-actions" style={{ marginTop: 16 }}>
                <button className="btn-secondary" onClick={() => {
                  if (previewBlobUrl) { try { URL.revokeObjectURL(previewBlobUrl); } catch (_) {} setPreviewBlobUrl(null); }
                  setSelectedAttachment(null);
                }}>Close</button>
                <button className="btn-secondary" onClick={() => viewAttachmentInNewTab(selectedAttachment.file, () => handleDownloadAttachment(selectedAttachment.entry, selectedAttachment.file))}>
                  Open in New Tab
                </button>
                <button className="btn-primary" onClick={() => handleDownloadAttachment(selectedAttachment.entry, selectedAttachment.file)}>
                  Download
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Correspondence</h2>
              <button className="btn-close" onClick={() => setShowEditModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <ConfiguredField fieldKey="project_id" formConfig={editSubjectFormConfig} defaultLabel="Project" defaultRequired>
                  <select
                    value={editForm.project_id}
                    onChange={(e) => setEditForm({ ...editForm, project_id: e.target.value })}
                  >
                    <option value="">Select Project</option>
                    {projectOptions.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </ConfiguredField>
                <ConfiguredField fieldKey="status" formConfig={editSubjectFormConfig} defaultLabel="Status">
                  <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                    <option value="Open">Open</option>
                    <option value="Closed">Closed</option>
                  </select>
                </ConfiguredField>
              </div>
              <ConfiguredField fieldKey="subject" formConfig={editSubjectFormConfig} defaultLabel="Subject" defaultRequired>
                <input
                  type="text"
                  value={editForm.subject}
                  onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                  placeholder="Correspondence subject"
                />
              </ConfiguredField>
              <div className="form-row">
                <ConfiguredField fieldKey="category" formConfig={editSubjectFormConfig} defaultLabel="Category">
                  <select value={editForm.category} onChange={(e) => handleEditCategoryChange(e.target.value)}>
                    {correspondenceCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </ConfiguredField>
                <div className="form-group">
                  <label>Location / block</label>
                  <input
                    type="text"
                    value={editForm.location_block || ''}
                    onChange={(e) => setEditForm({ ...editForm, location_block: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="severity" formConfig={editSubjectFormConfig} defaultLabel="Priority">
                  <select value={editForm.severity} onChange={(e) => setEditForm({ ...editForm, severity: e.target.value })}>
                    <option value="Immediate">Immediate</option>
                    <option value="Urgent">Urgent</option>
                    <option value="Routine">Routine</option>
                    <option value="Critical">Critical</option>
                  </select>
                </ConfiguredField>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Targeted Reply (Days)</label>
                  <input
                    type="number"
                    min={0}
                    value={editForm.targeted_reply_days}
                    onChange={(e) => setEditForm({ ...editForm, targeted_reply_days: parseInt(e.target.value, 10) || 0 })}
                  />
                </div>
                <div className="form-group">
                  <label>Last letter date</label>
                  <input
                    type="date"
                    value={editForm.last_entry_date}
                    onChange={(e) => setEditForm({ ...editForm, last_entry_date: e.target.value })}
                  />
                </div>
              </div>
              {getCategoryFieldConfig(editForm.category).length > 0 && (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: 13, color: '#374151' }}>
                    Category-specific details ({editForm.category})
                  </p>
                  {editFormLoadingLinked ? (
                    <p style={{ fontSize: 13, color: '#6b7280' }}>Loading...</p>
                  ) : (
                    <div className="form-row" style={{ flexWrap: 'wrap' }}>
                      {getCategoryFieldConfig(editForm.category).map((field) => (
                        <div className="form-group" key={field.key} style={{ minWidth: 220, flex: '1 1 260px' }}>
                          <label>{field.label}{field.required && <span className="req"> *</span>}</label>
                          {field.type === 'select' ? (
                            <select
                              value={(editForm.category_data || {})[field.key] ?? ''}
                              onChange={(e) => handleEditCategoryFieldChange(field.key, e.target.value, 'text')}
                            >
                              <option value="">Select {field.label}</option>
                              {(field.options || []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : field.type === 'textarea' ? (
                            <textarea
                              value={(editForm.category_data || {})[field.key] ?? ''}
                              onChange={(e) => handleEditCategoryFieldChange(field.key, e.target.value, 'text')}
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                              rows={3}
                            />
                          ) : (
                            <input
                              type={field.type}
                              value={(editForm.category_data || {})[field.key] ?? ''}
                              onChange={(e) => handleEditCategoryFieldChange(field.key, e.target.value, field.type)}
                              placeholder={field.type === 'text' ? `Enter ${field.label.toLowerCase()}` : ''}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {editSubjectFormConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={editSubjectFormConfig.customFields}
                  values={subjectCustomValues}
                  onChange={onSubjectCustomChange}
                  title="Additional fields"
                />
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleSaveEdit}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAppendModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Append Entry</h2>
              <button className="btn-close" onClick={() => setShowAppendModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <ConfiguredField fieldKey="direction" formConfig={appendEntryFormConfig} defaultLabel="Direction">
                  <select
                    value={newEntry.direction}
                    onChange={(e) => {
                      const dir = e.target.value;
                      setNewEntry({
                        ...newEntry,
                        direction: dir,
                        from: dir === 'Outward' ? DEC_INFRA : '',
                        to: dir === 'Inward' ? DEC_INFRA : '',
                      });
                    }}
                  >
                    <option value="Outward">Outward</option>
                    <option value="Inward">Inward</option>
                  </select>
                </ConfiguredField>
                <ConfiguredField fieldKey="letter_date" formConfig={appendEntryFormConfig} defaultLabel="Letter/Email Date" defaultRequired>
                  <input type="date" value={newEntry.letter_date} onChange={(e) => setNewEntry({ ...newEntry, letter_date: e.target.value })} />
                </ConfiguredField>
                <ConfiguredField fieldKey="mode" formConfig={appendEntryFormConfig} defaultLabel="Mode">
                  <select
                    value={normalizeMode(newEntry.mode)}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setNewEntry({ ...newEntry, mode });
                      if (mode === 'Email') setEntryError('');
                    }}
                  >
                    <option value="Email">Email</option>
                    <option value="Letter">Letter</option>
                  </select>
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField
                  fieldKey="ref_no"
                  formConfig={appendEntryFormConfig}
                  defaultLabel="Letter Reference No"
                  defaultRequired={!isEmailCorrespondenceMode(newEntry.mode)}
                >
                  <input
                    type="text"
                    value={newEntry.ref_no}
                    onChange={(e) => setNewEntry({ ...newEntry, ref_no: e.target.value })}
                    placeholder={isEmailCorrespondenceMode(newEntry.mode) ? 'Optional for email' : 'e.g. NH48/PH2/OUT/001'}
                  />
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="from" formConfig={appendEntryFormConfig} defaultLabel="From" defaultRequired>
                  {briefDesignations.length > 0 && newEntry.direction === 'Inward' ? (
                    <>
                      <select
                        value={briefDesignations.includes(newEntry.from) ? newEntry.from : '__other__'}
                        onChange={(e) => setNewEntry({ ...newEntry, from: e.target.value === '__other__' ? '' : e.target.value })}
                      >
                        {briefDesignations.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                        <option value="__other__">— Other (enter manually) —</option>
                      </select>
                      <input type="text" value={newEntry.from} onChange={(e) => setNewEntry({ ...newEntry, from: e.target.value })} placeholder="Or type sender manually" style={{ marginTop: 6 }} />
                    </>
                  ) : briefDesignations.length > 0 && newEntry.direction === 'Outward' ? (
                    <input type="text" value={DEC_INFRA} readOnly style={{ background: '#f3f4f6', cursor: 'default' }} title="Outward: From is always DEC Infra" />
                  ) : (
                    <input type="text" value={newEntry.from} onChange={(e) => setNewEntry({ ...newEntry, from: e.target.value })} placeholder="Sender" />
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="to" formConfig={appendEntryFormConfig} defaultLabel="To" defaultRequired>
                  {briefDesignations.length > 0 && newEntry.direction === 'Outward' ? (
                    <>
                      <select
                        value={briefDesignations.includes(newEntry.to) ? newEntry.to : '__other__'}
                        onChange={(e) => setNewEntry({ ...newEntry, to: e.target.value === '__other__' ? '' : e.target.value })}
                      >
                        {briefDesignations.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                        <option value="__other__">— Other (enter manually) —</option>
                      </select>
                      <input type="text" value={newEntry.to} onChange={(e) => setNewEntry({ ...newEntry, to: e.target.value })} placeholder="Or type recipient manually" style={{ marginTop: 6 }} />
                    </>
                  ) : briefDesignations.length > 0 && newEntry.direction === 'Inward' ? (
                    <input type="text" value={DEC_INFRA} readOnly style={{ background: '#f3f4f6', cursor: 'default' }} title="Inward: To is always DEC Infra" />
                  ) : (
                    <input type="text" value={newEntry.to} onChange={(e) => setNewEntry({ ...newEntry, to: e.target.value })} placeholder="Recipient" />
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Priority</label>
                  <select value={normalizePriority(newEntry.priority, normalizePriority(chain.severity))} onChange={(e) => setNewEntry({ ...newEntry, priority: e.target.value })}>
                    <option value="Immediate">Immediate</option>
                    <option value="Urgent">Urgent</option>
                    <option value="Routine">Routine</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Targeted Reply (Days)</label>
                  <input type="number" value={newEntry.sla_days} onChange={(e) => setNewEntry({ ...newEntry, sla_days: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="form-group" />
              </div>
              <ConfiguredField fieldKey="summary" formConfig={appendEntryFormConfig} defaultLabel="Summary" defaultRequired>
                <textarea value={newEntry.summary} onChange={(e) => setNewEntry({ ...newEntry, summary: e.target.value })} placeholder="Brief description of this entry..." rows={3} />
              </ConfiguredField>
              <ConfiguredField fieldKey="attachments" formConfig={appendEntryFormConfig} defaultLabel="Attachments" defaultRequired>
                <input
                  type="file"
                  multiple
                  onChange={handleAppendAttachmentChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.msg,.eml"
                  disabled={attachmentUploading}
                />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {newEntry.attachments.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {newEntry.attachments.map((file, idx) => (
                      <div key={`${file.name}-${file.size}-${idx}`} style={{ fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '6px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{file.name}</span>
                        <span style={{ color: '#22c55e', marginRight: 6 }}>✓</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => handleRemoveAppendAttachment(idx)} title="Remove attachment">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </ConfiguredField>
              <div className="form-group">
                <label>
                  <input type="checkbox" checked={newEntry.response_expected} onChange={(e) => setNewEntry({ ...newEntry, response_expected: e.target.checked })} style={{ marginRight: 8 }} />
                  Response Expected
                </label>
              </div>
              {appendEntryFormConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={appendEntryFormConfig.customFields}
                  values={entryCustomValues}
                  onChange={onEntryCustomChange}
                  title="Additional fields"
                />
              )}
              {entryError && <p style={{ color: '#dc2626', fontSize: 12 }}>{entryError}</p>}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowAppendModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleAppendEntry} disabled={attachmentUploading}>Append Entry</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditEntryModal && editEntryTarget && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Entry</h2>
              <button className="btn-close" onClick={() => { setShowEditEntryModal(false); setEditEntryTarget(null); }}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <ConfiguredField fieldKey="direction" formConfig={editEntryFormConfig} defaultLabel="Direction">
                  <select
                    value={editEntryForm.direction}
                    onChange={(e) => {
                      const dir = e.target.value;
                      setEditEntryForm({
                        ...editEntryForm,
                        direction: dir,
                        from: dir === 'Outward' ? DEC_INFRA : editEntryForm.from,
                        to: dir === 'Inward' ? DEC_INFRA : editEntryForm.to,
                      });
                    }}
                  >
                    <option value="Outward">Outward</option>
                    <option value="Inward">Inward</option>
                  </select>
                </ConfiguredField>
                <ConfiguredField fieldKey="letter_date" formConfig={editEntryFormConfig} defaultLabel="Letter/Email Date" defaultRequired>
                  <input type="date" value={editEntryForm.letter_date} onChange={(e) => setEditEntryForm({ ...editEntryForm, letter_date: e.target.value })} />
                </ConfiguredField>
                <ConfiguredField fieldKey="mode" formConfig={editEntryFormConfig} defaultLabel="Mode">
                  <select
                    value={normalizeMode(editEntryForm.mode)}
                    onChange={(e) => {
                      const mode = e.target.value;
                      setEditEntryForm({ ...editEntryForm, mode });
                      if (mode === 'Email') setEditEntryError('');
                    }}
                  >
                    <option value="Email">Email</option>
                    <option value="Letter">Letter</option>
                  </select>
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField
                  fieldKey="ref_no"
                  formConfig={editEntryFormConfig}
                  defaultLabel="Letter Reference No"
                  defaultRequired={!isEmailCorrespondenceMode(editEntryForm.mode)}
                >
                  <input
                    type="text"
                    value={editEntryForm.ref_no}
                    onChange={(e) => setEditEntryForm({ ...editEntryForm, ref_no: e.target.value })}
                    placeholder={isEmailCorrespondenceMode(editEntryForm.mode) ? 'Optional for email' : 'e.g. NH48/PH2/OUT/001'}
                  />
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="from" formConfig={editEntryFormConfig} defaultLabel="From" defaultRequired>
                  {briefDesignations.length > 0 && editEntryForm.direction === 'Inward' ? (
                    <>
                      <select
                        value={briefDesignations.includes(editEntryForm.from) ? editEntryForm.from : '__other__'}
                        onChange={(e) => setEditEntryForm({ ...editEntryForm, from: e.target.value === '__other__' ? '' : e.target.value })}
                      >
                        {briefDesignations.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                        <option value="__other__">— Other (enter manually) —</option>
                      </select>
                      <input type="text" value={editEntryForm.from} onChange={(e) => setEditEntryForm({ ...editEntryForm, from: e.target.value })} placeholder="Or type sender manually" style={{ marginTop: 6 }} />
                    </>
                  ) : briefDesignations.length > 0 && editEntryForm.direction === 'Outward' ? (
                    <input type="text" value={DEC_INFRA} readOnly style={{ background: '#f3f4f6', cursor: 'default' }} title="Outward: From is always DEC Infra" />
                  ) : (
                    <input type="text" value={editEntryForm.from} onChange={(e) => setEditEntryForm({ ...editEntryForm, from: e.target.value })} placeholder="Sender" />
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="to" formConfig={editEntryFormConfig} defaultLabel="To" defaultRequired>
                  {briefDesignations.length > 0 && editEntryForm.direction === 'Outward' ? (
                    <>
                      <select
                        value={briefDesignations.includes(editEntryForm.to) ? editEntryForm.to : '__other__'}
                        onChange={(e) => setEditEntryForm({ ...editEntryForm, to: e.target.value === '__other__' ? '' : e.target.value })}
                      >
                        {briefDesignations.map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                        <option value="__other__">— Other (enter manually) —</option>
                      </select>
                      <input type="text" value={editEntryForm.to} onChange={(e) => setEditEntryForm({ ...editEntryForm, to: e.target.value })} placeholder="Or type recipient manually" style={{ marginTop: 6 }} />
                    </>
                  ) : briefDesignations.length > 0 && editEntryForm.direction === 'Inward' ? (
                    <input type="text" value={DEC_INFRA} readOnly style={{ background: '#f3f4f6', cursor: 'default' }} title="Inward: To is always DEC Infra" />
                  ) : (
                    <input type="text" value={editEntryForm.to} onChange={(e) => setEditEntryForm({ ...editEntryForm, to: e.target.value })} placeholder="Recipient" />
                  )}
                </ConfiguredField>
              </div>
              <ConfiguredField fieldKey="summary" formConfig={editEntryFormConfig} defaultLabel="Summary" defaultRequired>
                <textarea value={editEntryForm.summary} onChange={(e) => setEditEntryForm({ ...editEntryForm, summary: e.target.value })} placeholder="Brief description..." rows={3} />
              </ConfiguredField>
              <ConfiguredField fieldKey="attachments" formConfig={editEntryFormConfig} defaultLabel="Attachments" defaultRequired>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 6px' }}>Max {correspondenceMaxSizeLabel()} per file. Please compress larger files before uploading.</p>
                <input
                  type="file"
                  multiple
                  onChange={handleEditEntryAttachmentChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.msg,.eml"
                  disabled={attachmentUploading}
                />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {(editEntryForm.attachments || []).length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(editEntryForm.attachments || []).map((file, idx) => (
                      <div key={`edit-${file.name}-${file.size}-${idx}`} style={{ fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '6px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{file.name}</span>
                        <span style={{ color: '#22c55e', marginRight: 6 }}>✓</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => handleRemoveEditEntryAttachment(idx)} title="Remove attachment">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </ConfiguredField>
              {editEntryFormConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={editEntryFormConfig.customFields}
                  values={editEntryCustomValues}
                  onChange={onEditEntryCustomChange}
                  title="Additional fields"
                />
              )}
              {editEntryError && <p style={{ color: '#dc2626', fontSize: 12 }}>{editEntryError}</p>}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => { setShowEditEntryModal(false); setEditEntryTarget(null); }}>Cancel</button>
                <button className="btn-primary" onClick={handleUpdateEntry} disabled={attachmentUploading}>Update Entry</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreadDetail;
